import { z } from 'zod';

/**
 * Zod schema for the CreateAppointment form.
 * This is the FORM shape — not the API request shape.
 * transform-form-to-request.ts maps this to CreateAppointmentRequest.
 */

const codingSchema = z.object({
  code: z.string(),
  display: z.string(),
  // Backend value-sets sometimes ship `definition: null`. `.optional()`
  // accepts `undefined` only, so without `.nullable()` the picker's null
  // arrives as a Zod error and Save stays disabled.
  definition: z.string().nullable().optional(),
});

const locationSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const appointmentFormSchema = z
  .object({
    // Location
    location: locationSchema.nullable(),

    // Provider
    providerId: z.string().nullable(),
    providerName: z.string().nullable(),
    providerSpeciality: z.string().nullable(),

    // Date & Time
    selectedDate: z.string().nullable(),
    slotId: z.string().nullable(),
    slotStart: z.string().nullable(),
    slotEnd: z.string().nullable(),

    // Appointment Profile
    appointmentProfileId: z.string().nullable(),
    appointmentProfileName: z.string().nullable(),
    /** Hex/ARGB color string from `appointmentProfile.color` — drives the dot. */
    appointmentProfileColor: z.string().nullable(),
    /** AppointmentType from picked profile — drives FOLLOWUP-only sections
     * (Previous Appointment, save-rule referenceAppointmentId requirement). */
    appointmentType: codingSchema.nullable(),
    reasonCode: codingSchema.nullable(),
    /** RFV options for the picked profile — surfaced as a dropdown in the
     * profile card. Auto-populated by PreBookSelectedAppointmentProfile.
     * (Flutter `providerReasonForVisits` on the response.) */
    reasonCodeOptions: z.array(codingSchema).default([]),
    minutesDuration: z.number().nullable(),

    // Patient — picked details cached so the selected card can render
    // without a separate fetch (avatar / dob / age / gender / importance).
    patientId: z.string().nullable(),
    patientName: z.string().nullable(),
    patientPhoto: z.string().nullable(),
    patientDob: z.string().nullable(),
    patientAge: z.string().nullable(),
    patientGender: z.string().nullable(),
    patientImportanceCode: z.string().nullable(),
    patientIsMissingData: z.boolean().default(false),
    isRelatedPerson: z.boolean().default(false),

    // Emergency contacts — auto-populated from PreBookSelectedPatient.
    // Drives the CALLBACK CONTACT section's grid of pickable contacts.
    // `relation` keeps the full master-list shape so the save endpoint
    // can re-ship it as `{ Id, Name, ... }` (PascalCase) for both
    // existing rows and new rows added via CreateContactView.
    emergencyContacts: z
      .array(
        z.object({
          id: z.string().optional(),
          emergencyContactId: z.string().optional(),
          name: z.string().optional(),
          firstName: z.string().optional(),
          middleName: z.string().optional(),
          lastName: z.string().optional(),
          relationship: z.string().optional(),
          relation: z
            .object({
              id: z.string().optional(),
              name: z.string().optional(),
              code: z.string().optional(),
              system: z.string().optional(),
            })
            .optional(),
          phone: z.string().optional(),
          contactNumber: z.string().optional(),
          photo: z.string().optional(),
          photoUrl: z.string().optional(),
          isSelf: z.boolean().optional(),
          submittedDate: z.string().nullable().optional(),
          building: z.string().nullable().optional(),
          apartment: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
          city: z.string().nullable().optional(),
          address: z.string().nullable().optional(),
          latitude: z.string().nullable().optional(),
          longitude: z.string().nullable().optional(),
        }),
      )
      .default([]),

    // Payment
    sponsorType: z
      .enum(['Self-Pay', 'Insurance', 'Corporate'])
      .nullable()
      .default('Self-Pay'),
    sponsorId: z.string().nullable(),
    policyNo: z.string().nullable(),
    /**
     * Picked sponsor's full payer object — Flutter
     * `appointment.sponsor = insuranceList.insuranceProvider` /
     * `corporateList.corporateProvider` (payment_widget.dart:209/239).
     * Re-shipped on the BookAppointment payload.
     */
    sponsor: z
      .object({
        id: z.string().optional(),
        payerName: z.string().optional(),
        name: z.string().optional(),
        imageUrl: z.string().optional(),
        // Payer's DHPO/billing company code + related TPAs. Carried through
        // from the selected insurance/corporate payer so the save ships a
        // COMPLETE sponsor — a missing payerCompanyId is stored as null and
        // breaks eRx→DHPO (empty ReceiverID/PayerID). Flutter parity.
        payerCompanyId: z.string().nullish(),
        relatedTpa: z.array(z.unknown()).nullish(),
      })
      .nullable()
      .default(null),
    /** Patient-level insurance + corporate lists, populated from PreBookSelectedPatient. */
    healthInsurances: z
      .array(z.any())
      .default([]),
    corporateAgreements: z
      .array(z.any())
      .default([]),

    // Flags — `flagIds` keeps the existing string-id contract; `patientFlags`
    // holds the rich FlagResponse[] auto-populated by PreBookSelectedPatient
    // and is what gets serialized to the booking payload.
    flagIds: z.array(z.string()).default([]),
    patientFlags: z
      .array(
        z.object({
          code: z.union([z.number(), z.string()]).optional(),
          display: z.string().optional(),
          categoryName: z.string().optional(),
        }),
      )
      .default([]),

    // Invoice — auto-populated by PreBookSelectedPatient.prices and updated
    // by PreBookCalculatePrice on payment-type / sponsor change.
    invoiceTotals: z
      .object({
        grossTotal: z.number().optional(),
        discountTotal: z.number().optional(),
        netTotal: z.number().optional(),
        patientPayable: z.number().optional(),
        sponsorOutstanding: z.number().optional(),
      })
      .nullable()
      .default(null),

    // Note — null until the user reveals the section via the bottom-row
    // "Note" icon. Mirrors Flutter `appointment.comment != null`
    // (create_appointment_widget.dart:1640).
    comment: z.string().max(500).nullable().default(null),

    // Questionnaire / Send Form — picker filters to status === 'Active'
    // (Flutter forms_selection_widget.dart:66) but we cache the status on
    // the picked form so the card can render the same chip Flutter shows.
    questionnaireId: z.string().nullable(),
    questionnaireName: z.string().nullable(),
    questionnaireStatus: z.string().nullable(),

    // Reminder — auto-populated by PreBookSelectedAppointmentProfile
    // (Flutter saveAppointmentButtonStatus hard-requires it for save).
    reminderProfileId: z.string().nullable(),
    reminderProfileName: z.string().nullable(),
    /** Per-rule list ("In-App 1 hour before" etc.) from `reminderProfileType[]`. */
    reminderProfileRules: z.array(z.string()).default([]),
    /** Full reminder profile snapshot — drives the editor view (Flutter
     * `edit_reminder_profile_widget.dart`). Cloned on pencil tap, mutated
     * locally, written back on Save. The flat id/name/rules fields above
     * stay synced for the card render and the booking payload. */
    reminderProfile: z
      .object({
        id: z.string().optional(),
        profileName: z.string().optional(),
        customNote: z.string().optional(),
        enableInAppBooking: z.boolean().optional(),
        isDefault: z.boolean().optional(),
        reminderProfileType: z
          .array(
            z.object({
              id: z.string().optional(),
              type: z.string().optional(),
              number: z.string().optional(),
              time: z.string().optional(),
              whenToSend: z.string().optional(),
            }),
          )
          .optional(),
      })
      .nullable()
      .default(null),

    // Billing — comes from PreBookSelectedAppointmentProfile.billingProfiles.
    // billingProfilesList caches the full set so the dropdown popover can
    // render alternatives without re-fetching.
    /** Service ids the user added via the "+ Add Procedure Codes" popup.
     * Mirrors Flutter `appointment.addedServices` / `selectedServices()` —
     * the booking payload sends these alongside the chosen billing profile.
     * `serviceType` ("Default" for provider-fee rows, "all" for catalog
     * rows) drives `IndividualService.type` on PreBookCalculatePrice. */
    addedServices: z
      .array(
        z.object({
          id: z.string(),
          serviceType: z.enum(['Default', 'all']),
          procedureCode: z.string().optional(),
          displayName: z.string().optional(),
          fee: z.number().optional(),
        }),
      )
      .default([]),

    billingProfileId: z.string().nullable(),
    billingProfileName: z.string().nullable(),

    /**
     * Snapshot of the picked appointment profile — Flutter sends this
     * full object as `appointment.appointmentProfile` on Book. Captured
     * on profile pick (appointment-profile-section / provider-selection-
     * view / time-date-selection-view) so the transform can serialise
     * id / specialityId / billingProfileId / roomId / color / etc.
     * verbatim instead of reconstructing from flat form fields.
     */
    pickedProfile: z.any().nullable().default(null),

    /**
     * Picked provider's speciality in the SNOMED wire shape Flutter
     * ships on Book — `{ specialitySkillId, code: { code, displayName,
     * system, ... } }`. The backend's gRPC GetSpecialitiesByCodesAsync
     * looks the appointment up by `code.code`, so a hardcoded "general"
     * crashes the next read. Captured on provider pick.
     */
    providerSpecialityCode: z.any().nullable().default(null),

    /**
     * Snapshot of the picked patient's residential address — Flutter
     * ships the full PatientAddress object on Book for Home visits.
     */
    residentialAddress: z.any().nullable().default(null),
    billingProfilesList: z
      .array(
        z.object({
          id: z.string().optional(),
          name: z.string().optional(),
          totalAmount: z.number().optional(),
          totalCost: z.number().optional(),
          isDefault: z.boolean().optional(),
        }),
      )
      .default([]),

    // Recurrence — backend period enum is `Days | Weeks | Months` (Flutter
    // app_constants.dart:366-368). `Years` was kept as a UI-only forward
    // option but the backend rejects it; the picker only exposes the three
    // accepted values. `days` carries full weekday names ("Monday" etc.)
    // and is sent only when `period === 'Weeks'`. `slots` is the confirmed
    // occurrence list from POST /api/Appointment/RetrieveSlots — written
    // on Save in the configurator.
    recurring: z
      .object({
        startDate: z.string(),
        endDate: z.string(),
        repeatEvery: z.number().min(1),
        numberOfRepeat: z.number().min(0),
        period: z.enum(['Days', 'Weeks', 'Months', 'Years']),
        days: z.array(z.string()).optional(),
        // Slot shape must match Flutter's `AppointmentSlot` wire so the
        // backend's ConvertCreateAppointmentCommandTimeToUtc can iterate
        // without NPE. The `{ start, end }` reduction we used before
        // dropped `practiceId` / `providerId` / `overbooked` /
        // `appointmentType` / `comment` / `practiceName`, one of which
        // the backend dereferences during UTC conversion (PROD-961).
        // Slot shape matches `RecurringSlot` (the null-tolerant wire
        // variant) so the backend's
        // ConvertCreateAppointmentCommandTimeToUtc can iterate without
        // NPE (PROD-961). The outer `.nullable()` on `appointmentType`
        // lets Flutter's `"appointmentType": null` flow through; inner
        // fields stay non-nullable to match `AppointmentValueSets`.
        slots: z
          .array(
            z.object({
              appointmentType: z
                .object({
                  code: z.string().optional(),
                  display: z.string().optional(),
                  definition: z.string().optional(),
                })
                .optional()
                .nullable(),
              start: z.string().optional(),
              end: z.string().optional(),
              comment: z.string().optional().nullable(),
              overbooked: z.boolean().optional(),
              practiceId: z.string().optional(),
              providerId: z.string().optional(),
              practiceName: z.string().optional().nullable(),
            }),
          )
          .optional(),
      })
      .nullable()
      .default(null),

    // Home visit — addressList comes from PreBookSelectedPatient.patientAddressList;
    // residentialAddressId defaults to first isDefault entry when location is Home.
    residentialAddressId: z.string().nullable(),
    // Shape A (picker list) — verified against dev curl 2026-05-20.
    // Mirrors Flutter `AppointmentPatientAddress` (16 fields). Title
    // source: `isResidential ? locationName : name`. fullAddress is a
    // client-side computation over `apartment + address + city + country`
    // — empty entries should be filtered before rendering (Flutter bloc
    // parity at `scheduling_bloc.dart:973`).
    //
    // Field-by-field this MUST stay in lockstep with the `PatientAddress`
    // interface in `src/services/scheduling/use-prebook-selected-patient.ts`
    // (which carries the canonical wire-sample comment). A drift here
    // silently re-breaks the picker — when adding/removing a field,
    // update both.
    addressList: z
      .array(
        z.object({
          id: z.string().optional(),
          name: z.string().optional(),
          imageUrl: z.string().nullable().optional(),
          building: z.string().optional(),
          apartment: z.string().optional(),
          country: z.string().optional(),
          city: z.string().optional(),
          address: z.string().optional(),
          latitude: z.string().nullable().optional(),
          longitude: z.string().nullable().optional(),
          isDefault: z.boolean().optional(),
          phoneNumber: z.string().nullable().optional(),
          relation: z
            .object({ id: z.string().optional(), name: z.string().optional() })
            .optional(),
          locationName: z.string().optional(),
          notes: z.string().nullable().optional(),
          isResidential: z.boolean().optional(),
        }),
      )
      .default([]),
    callbackContactId: z.string().nullable(),

    // Follow-up — display fields cached for the "selected" card after pick;
    // set by PreviousAppointmentSection.onSelect.
    referenceAppointmentId: z.string().nullable(),
    previousAppointmentRFV: z.string().nullable(),
    previousAppointmentLocation: z
      .object({ id: z.string().optional(), name: z.string().optional() })
      .nullable(),
    previousAppointmentDate: z.string().nullable(),
    /** Flutter sets this to false when the appointment was seeded from
     * a follow-up request — hides the delete (×) on the selected card. */
    previousAppointmentDeletable: z.boolean().default(true),
    /**
     * FU Request id when the appointment was seeded from an FU Requests
     * row (PROD-920). Ships on the wire as `followUpId` so the backend
     * can link the saved appointment back to the originating FU
     * request. Mirrors Flutter `calendar_screen.dart:179`
     * `followUpId: request.id`.
     *
     * Distinct from `referenceAppointmentId` (= the ORIGINAL
     * appointment's id) — both are sent for FU saves.
     */
    followUpId: z.string().nullable(),

    // Payment link (home/video)
    paymentLink: z.string().nullable(),

    // Priority
    priority: z.number().default(0),

    // Description
    description: z.string().max(200).default(''),
  })
  .superRefine((data, ctx) => {
    // FOLLOWUP create requires referenceAppointmentId
    // (Flutter saveAppointmentButtonStatus:1339–1346 — create branch).
    if (data.appointmentType?.code === 'FOLLOWUP' && !data.referenceAppointmentId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Previous appointment is required for follow-ups',
        path: ['referenceAppointmentId'],
      });
    }
    // Provider required
    if (!data.providerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provider is required',
        path: ['providerId'],
      });
    }
    // Patient required
    if (!data.patientId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Patient is required',
        path: ['patientId'],
      });
    }
    // Slot required
    if (!data.slotId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Time slot is required',
        path: ['slotId'],
      });
    }
    // Profile required
    if (!data.appointmentProfileId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Appointment profile is required',
        path: ['appointmentProfileId'],
      });
    }
    // ReasonCode.code required (Flutter saveAppointmentButtonStatus:1336)
    if (!data.reasonCode?.code) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reason for visit is required',
        path: ['reasonCode'],
      });
    }
    // Reminder required — auto-populated by PreBookSelectedAppointmentProfile
    // (Flutter saveAppointmentButtonStatus:1358).
    if (!data.reminderProfileId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reminder profile is required',
        path: ['reminderProfileId'],
      });
    }
    // Residential address required for Home visits
    if (
      data.location?.name === 'Home' &&
      !data.residentialAddressId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Address is required for home visits',
        path: ['residentialAddressId'],
      });
    }
    // Sponsor required for Insurance/Corporate
    if (
      data.sponsorType &&
      data.sponsorType !== 'Self-Pay' &&
      !data.sponsorId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${data.sponsorType} agreement is required`,
        path: ['sponsorId'],
      });
    }
  });

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>;

export const defaultFormValues: AppointmentFormValues = {
  location: null,
  providerId: null,
  providerName: null,
  providerSpeciality: null,
  selectedDate: null,
  slotId: null,
  slotStart: null,
  slotEnd: null,
  appointmentProfileId: null,
  appointmentProfileName: null,
  appointmentProfileColor: null,
  appointmentType: null,
  reasonCode: null,
  reasonCodeOptions: [],
  minutesDuration: null,
  patientId: null,
  patientName: null,
  patientPhoto: null,
  patientDob: null,
  patientAge: null,
  patientGender: null,
  patientImportanceCode: null,
  patientIsMissingData: false,
  isRelatedPerson: false,
  emergencyContacts: [],
  sponsorType: 'Self-Pay',
  sponsorId: null,
  policyNo: null,
  sponsor: null,
  healthInsurances: [],
  corporateAgreements: [],
  flagIds: [],
  patientFlags: [],
  invoiceTotals: null,
  comment: null,
  questionnaireId: null,
  questionnaireName: null,
  questionnaireStatus: null,
  reminderProfileId: null,
  reminderProfileName: null,
  reminderProfileRules: [],
  reminderProfile: null,
  addedServices: [],
  billingProfileId: null,
  billingProfileName: null,
  pickedProfile: null,
  providerSpecialityCode: null,
  residentialAddress: null,
  billingProfilesList: [],
  recurring: null,
  residentialAddressId: null,
  addressList: [],
  callbackContactId: null,
  referenceAppointmentId: null,
  previousAppointmentRFV: null,
  previousAppointmentLocation: null,
  previousAppointmentDate: null,
  previousAppointmentDeletable: true,
  followUpId: null,
  paymentLink: null,
  priority: 0,
  description: '',
};
